<!-- Single host for the app dialogs (appAlert/appConfirm/appPrompt) — mounted
     once in App.vue. Replaces native window.* dialogs with a design-system
     modal: overlay + card (same pattern as the Screens.vue modals and
     SynValuePrompt). Enter = OK, Esc / overlay click = cancel. -->
<template>
  <transition name="ad-fade">
    <div v-if="active" class="ad-overlay" @pointerdown.self="onCancel">
      <div class="ad-card" role="dialog" aria-modal="true" @keydown.esc.prevent="onCancel">
        <p v-if="active.title" class="ad-title">{{ active.title }}</p>
        <p class="ad-msg">{{ active.message }}</p>
        <input
          v-if="active.kind === 'prompt'"
          ref="inputEl"
          v-model="entry"
          class="ad-input"
          type="text"
          :placeholder="active.placeholder ?? ''"
          @keydown.enter.prevent="onOk"
        />
        <div class="ad-actions">
          <button v-if="active.kind !== 'alert'" type="button" class="ad-btn ghost" @click="onCancel">{{ active.cancelLabel ?? $t('common.cancel') }}</button>
          <button ref="okEl" type="button" class="ad-btn ok" :class="{ danger: active.danger }" @click="onOk" @keydown.enter.prevent="onOk">{{ active.okLabel ?? $t('common.ok') }}</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import { useAppDialog } from '../composables/useAppDialog';

const { active, settle } = useAppDialog();
const entry = ref('');
const inputEl = ref<HTMLInputElement | null>(null);
const okEl = ref<HTMLButtonElement | null>(null);

watch(active, (d) => {
  if (!d) return;
  entry.value = d.kind === 'prompt' ? (d.initial ?? '') : '';
  void nextTick(() => {
    if (d.kind === 'prompt') { inputEl.value?.focus(); inputEl.value?.select(); }
    else okEl.value?.focus();
  });
});

function onOk(): void {
  const d = active.value;
  if (!d) return;
  settle(d.kind === 'prompt' ? entry.value : true);
}
function onCancel(): void {
  const d = active.value;
  if (!d) return;
  settle(d.kind === 'alert' ? true : d.kind === 'prompt' ? null : false);
}
</script>

<style scoped>
.ad-overlay { position: fixed; inset: 0; z-index: 100; display: flex; align-items: center; justify-content: center; background: rgba(0, 0, 0, .45); padding: 16px; }
.ad-card { width: 360px; max-width: 92vw; background: var(--color-surface); border: 1px solid var(--color-line); border-radius: 12px; box-shadow: 0 20px 60px rgba(0, 0, 0, .35); padding: 16px; }
.ad-title { margin: 0 0 6px; font-size: .9rem; font-weight: 600; color: var(--color-ink); }
.ad-msg { margin: 0; font-size: .85rem; color: var(--color-ink); white-space: pre-line; line-height: 1.45; }
.ad-input { display: block; width: 100%; box-sizing: border-box; margin-top: 12px; border: 1px solid var(--color-line-strong); border-radius: 8px; padding: 8px 10px; font-size: .85rem; color: var(--color-ink); background: var(--color-surface-2); }
.ad-input:focus { outline: 2px solid var(--color-brand-600); outline-offset: -1px; }
.ad-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px; }
.ad-btn { min-width: 84px; padding: 8px 14px; border-radius: 8px; font-size: .82rem; font-weight: 600; cursor: pointer; border: 1px solid var(--color-line-strong); }
.ad-btn.ghost { background: var(--color-surface); color: var(--color-muted); }
.ad-btn.ghost:hover { background: var(--color-surface-2); color: var(--color-ink); }
.ad-btn.ok { background: var(--color-brand-600); border-color: var(--color-brand-600); color: #fff; }
.ad-btn.ok:hover { background: var(--color-brand-700); }
.ad-btn.ok.danger { background: var(--color-danger); border-color: var(--color-danger); }
.ad-fade-enter-active, .ad-fade-leave-active { transition: opacity .15s ease; }
.ad-fade-enter-from, .ad-fade-leave-to { opacity: 0; }
</style>
